import { useMemo, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FreeShippingProductsDialog } from "@/components/coupons/FreeShippingProductsDialog";
import { useUpsertCoupon } from "@/hooks/use-coupons";
import { useSellerProducts, useSellers } from "@/hooks/use-sellers";
import { describeCallableError } from "@/lib/callables";
import { toDate } from "@/lib/format";
import type { Coupon } from "@/types";

type DiscountType = "percentage" | "fixed" | "free_shipping";
type FreeShippingScope = "order" | "products";

const FREE_SHIPPING_SCOPES: { value: FreeShippingScope; title: string; description: string }[] = [
  {
    value: "order",
    title: "Em qualquer compra",
    description: "O frete sai grátis no pedido em que o cliente aplicar o cupom, pelo código ou no carrinho.",
  },
  {
    value: "products",
    title: "Em produtos do cardápio",
    description:
      "Quem comprar um dos produtos escolhidos não paga a entrega. O cupom fica sempre visível no carrinho.",
  },
];

/** `datetime-local` precisa de `YYYY-MM-DDTHH:mm` na hora LOCAL. */
function toInputValue(value: unknown): string {
  const d = toDate(value);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CouponDialog({
  coupon,
  open,
  onOpenChange,
}: {
  coupon?: Coupon;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const upsert = useUpsertCoupon();
  const sellers = useSellers();
  const isEdit = Boolean(coupon?.id);

  const [form, setForm] = useState(() => ({
    code: coupon?.code ?? "",
    description: coupon?.description ?? "",
    discountType: (coupon?.discountType ?? "percentage") as DiscountType,
    discount: String(coupon?.discount ?? ""),
    minValue: String(coupon?.minValue ?? ""),
    maxDiscount: String(coupon?.maxDiscount ?? ""),
    usageLimit: String(coupon?.usageLimit ?? ""),
    expirationDate: toInputValue(coupon?.expirationDate),
    establishment: coupon?.establishment ?? "",
    sellerId: coupon?.fromSellers?.[0] ?? "",
    isActive: coupon?.isActive ?? true,
    isVisibleAtCheckout: coupon?.isVisibleAtCheckout ?? false,
    // Cupom criado aqui é promoção do Zé Padoca por padrão. Num cupom que
    // já existe, vale o que está gravado (ausente era do estabelecimento).
    platformSponsored: coupon ? coupon.sponsoredBy === "platform" : true,
    // Frete grátis, no mesmo padrão do app do vendedor: em qualquer compra,
    // ou na compra de produtos escolhidos do cardápio.
    freeShippingScope: (coupon?.freeShippingScope ?? "order") as FreeShippingScope,
    freeShippingProductIds: coupon?.freeShippingProductIds ?? [],
  }));
  const [choosingProducts, setChoosingProducts] = useState(false);

  const isFreeShipping = form.discountType === "free_shipping";
  const isProductFreeShipping = isFreeShipping && form.freeShippingScope === "products";

  // Nomes dos produtos escolhidos, para o cupom dizer o que tem frete grátis
  // sem abrir o diálogo de novo.
  const products = useSellerProducts(isProductFreeShipping ? form.sellerId : undefined);
  const selectedProductNames = useMemo(() => {
    const names = form.freeShippingProductIds
      .map((id) => products.data?.find((p) => p.id === id)?.name)
      .filter((name): name is string => Boolean(name));
    if (names.length <= 3) return names.join(", ");
    return `${names.slice(0, 3).join(", ")} e mais ${names.length - 3}`;
  }, [form.freeShippingProductIds, products.data]);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // Os produtos são do estabelecimento: trocar de estabelecimento descarta a
  // escolha, que nunca valeria no carrinho do outro.
  function setSeller(sellerId: string) {
    setForm((prev) => ({
      ...prev,
      sellerId,
      freeShippingProductIds: sellerId === prev.sellerId ? prev.freeShippingProductIds : [],
    }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const code = form.code.trim().toUpperCase();
    const discount = Number(form.discount);

    if (!code) {
      toast.error("O código do cupom é obrigatório.");
      return;
    }
    if (!isFreeShipping && (!Number.isFinite(discount) || discount <= 0)) {
      toast.error("Informe um desconto maior que zero.");
      return;
    }
    if (form.discountType === "percentage" && discount > 100) {
      toast.error("Um desconto percentual não pode passar de 100.");
      return;
    }
    if (isProductFreeShipping && !form.sellerId) {
      toast.error("Frete grátis por produto vale num estabelecimento: escolha qual.");
      return;
    }
    if (isProductFreeShipping && form.freeShippingProductIds.length === 0) {
      toast.error("Escolha ao menos um produto com frete grátis.");
      return;
    }

    const seller = sellers.data?.find((s) => s.id === form.sellerId);

    try {
      await upsert.mutateAsync({
        id: coupon?.id,
        code,
        description: form.description.trim(),
        discountType: form.discountType,
        // O frete grátis não tem valor: o desconto é a taxa de entrega.
        discount: isFreeShipping ? 0 : discount,
        freeShippingScope: isFreeShipping ? form.freeShippingScope : undefined,
        freeShippingProductIds: isProductFreeShipping ? form.freeShippingProductIds : undefined,
        minValue: form.minValue ? Number(form.minValue) : undefined,
        maxDiscount:
          form.discountType === "percentage" && form.maxDiscount
            ? Number(form.maxDiscount)
            : undefined,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : undefined,
        // A callable recebe ISO e converte para Timestamp no servidor: o tipo
        // do Firestore não atravessa uma chamada de function.
        expirationDate: form.expirationDate ? new Date(form.expirationDate).toISOString() : null,
        establishment: form.establishment.trim() || seller?.name || undefined,
        fromSellers: form.sellerId ? [form.sellerId] : [],
        isActive: form.isActive,
        isVisibleAtCheckout: isProductFreeShipping ? true : form.isVisibleAtCheckout,
        sponsoredBy: form.platformSponsored ? "platform" : "seller",
      });
      toast.success(isEdit ? "Cupom atualizado." : "Cupom criado.");
      onOpenChange(false);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar cupom" : "Novo cupom"}</DialogTitle>
          <DialogDescription>
            Um cupom visível no carrinho aparece sozinho para quem compra no estabelecimento, e
            dispara um aviso para quem o favoritou. Os demais só entram pelo código.
          </DialogDescription>
        </DialogHeader>

        <form id="coupon-form" onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cp-code">Código</Label>
              <Input
                id="cp-code"
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                className="font-mono uppercase"
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-seller">Estabelecimento</Label>
              <Select value={form.sellerId} onValueChange={setSeller}>
                <SelectTrigger id="cp-seller">
                  <SelectValue placeholder="Escolher" />
                </SelectTrigger>
                <SelectContent>
                  {(sellers.data ?? []).map((seller) => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.name || seller.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cp-desc">Descrição</Label>
            <Textarea
              id="cp-desc"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="O que o cliente lê no carrinho"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cp-type">Tipo</Label>
              <Select value={form.discountType} onValueChange={(v) => set("discountType", v as DiscountType)}>
                <SelectTrigger id="cp-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentual</SelectItem>
                  <SelectItem value="fixed">Valor fixo</SelectItem>
                  <SelectItem value="free_shipping">Frete grátis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isFreeShipping ? null : (
              <div className="space-y-1.5">
                <Label htmlFor="cp-discount">
                  {form.discountType === "percentage" ? "Desconto (%)" : "Desconto (R$)"}
                </Label>
                <Input
                  id="cp-discount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.discount}
                  onChange={(e) => set("discount", e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          {isFreeShipping ? (
            <fieldset className="space-y-2">
              <legend className="mb-1.5 text-small font-semibold">Onde vale o frete grátis</legend>
              {FREE_SHIPPING_SCOPES.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                >
                  <input
                    type="radio"
                    name="cp-free-shipping-scope"
                    className="mt-1 size-4 shrink-0 accent-primary"
                    checked={form.freeShippingScope === option.value}
                    onChange={() => set("freeShippingScope", option.value)}
                  />
                  <span className="min-w-0">
                    <span className="block text-small font-semibold">{option.title}</span>
                    <span className="block text-caption text-muted-foreground">{option.description}</span>
                  </span>
                </label>
              ))}

              {isProductFreeShipping ? (
                <div className="space-y-1.5 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={!form.sellerId}
                    onClick={() => setChoosingProducts(true)}
                  >
                    {form.freeShippingProductIds.length > 0
                      ? `Produtos com frete grátis (${form.freeShippingProductIds.length})`
                      : "Escolher produtos"}
                  </Button>
                  <p className="text-caption text-muted-foreground">
                    {!form.sellerId
                      ? "Escolha o estabelecimento para ver o cardápio."
                      : selectedProductNames || "Nenhum produto escolhido ainda."}
                  </p>
                </div>
              ) : null}
            </fieldset>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="cp-min">Valor mínimo</Label>
              <Input
                id="cp-min"
                type="number"
                min={0}
                step="0.01"
                value={form.minValue}
                onChange={(e) => set("minValue", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-max">Desconto máximo</Label>
              <Input
                id="cp-max"
                type="number"
                min={0}
                step="0.01"
                value={form.maxDiscount}
                onChange={(e) => set("maxDiscount", e.target.value)}
                disabled={form.discountType !== "percentage"}
                placeholder={isFreeShipping ? "Não se aplica" : undefined}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-limit">Limite de uso</Label>
              <Input
                id="cp-limit"
                type="number"
                min={0}
                value={form.usageLimit}
                onChange={(e) => set("usageLimit", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cp-exp">Validade</Label>
            <Input
              id="cp-exp"
              type="datetime-local"
              value={form.expirationDate}
              onChange={(e) => set("expirationDate", e.target.value)}
            />
          </div>

          <label className="flex items-center justify-between gap-4 rounded-md border border-primary/60 bg-primary/5 p-3">
            <span className="min-w-0">
              <span className="block text-small font-semibold">Promoção do Zé Padoca</span>
              <span className="block text-caption text-muted-foreground">
                {isFreeShipping
                  ? "A plataforma paga a entrega: a taxa que o estabelecimento deixou de cobrar volta como crédito na fatura do mês. Desligado, quem paga é o estabelecimento."
                  : "O desconto é pago pela plataforma: ele volta ao estabelecimento como crédito na fatura do mês, abatido da comissão. Desligado, quem paga é o estabelecimento."}
              </span>
            </span>
            <Switch
              checked={form.platformSponsored}
              onCheckedChange={(v) => set("platformSponsored", v)}
            />
          </label>

          <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
            <span className="min-w-0">
              <span className="block text-small font-semibold">Ativo</span>
              <span className="block text-caption text-muted-foreground">
                Cupom inativo não pode ser aplicado.
              </span>
            </span>
            <Switch checked={form.isActive} onCheckedChange={(v) => set("isActive", v)} />
          </label>

          <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
            <span className="min-w-0">
              <span className="block text-small font-semibold">Visível no carrinho</span>
              <span className="block text-caption text-muted-foreground">
                {isProductFreeShipping
                  ? "Frete grátis por produto vale para todos os clientes: fica sempre visível no carrinho de quem compra os produtos escolhidos."
                  : "Aparece sozinho para quem compra no estabelecimento. Ativar isso dispara um push para quem o favoritou, uma vez a cada duas horas."}
              </span>
            </span>
            <Switch
              checked={isProductFreeShipping || form.isVisibleAtCheckout}
              onCheckedChange={(v) => set("isVisibleAtCheckout", v)}
              disabled={isProductFreeShipping}
            />
          </label>
        </form>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="coupon-form" disabled={upsert.isPending}>
            {upsert.isPending ? <Loader2 className="animate-spin" /> : null}
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Fora do <form>: o diálogo de produtos tem os próprios botões, e um
          Confirmar dentro do formulário enviaria o cupom. */}
      <FreeShippingProductsDialog
        open={choosingProducts}
        onOpenChange={setChoosingProducts}
        sellerId={form.sellerId}
        selectedIds={form.freeShippingProductIds}
        onConfirm={(ids) => {
          set("freeShippingProductIds", ids);
          setChoosingProducts(false);
        }}
      />
    </Dialog>
  );
}
